'use client'
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import type { LayoutModeType, LayoutOffcanvasStatesType, LayoutState, LayoutType, MenuType, ThemeType, TopBarThemeType } from '../types/context'

import useLocalStorage from '../hooks/useLocalStorage'
import useQueryParams from '../hooks/useQueryParams'
import { ChildrenType } from '../types/component-props'
import { toggleDocumentAttribute } from '../utils/layout'

const ThemeContext = createContext<LayoutType | undefined>(undefined)

const useLayoutContext = () => {
  const context = use(ThemeContext)
  if (!context) {
    throw new Error('useLayoutContext can only be used within LayoutProvider')
  }
  return context
}

const LayoutProvider = ({ children }: ChildrenType) => {
  const queryParams = useQueryParams()

  const override = !!(queryParams.layout_theme || queryParams.topbar_theme || queryParams.menu_theme || queryParams.menu_size || queryParams.layout_mode)

  const INIT_STATE: LayoutState = {
    theme: queryParams['layout_theme'] ? (queryParams['layout_theme'] as ThemeType) : 'light',
    topbarTheme: queryParams['topbar_theme'] ? (queryParams['topbar_theme'] as TopBarThemeType) : 'light',
    menu: {
      theme: queryParams['menu_theme'] ? (queryParams['menu_theme'] as MenuType['theme']) : 'dark',
      size: queryParams['menu_size'] ? (queryParams['menu_size'] as MenuType['size']) : 'sm-hover-active',
    },
    layoutMode: queryParams['layout_mode'] ? (queryParams['layout_mode'] as LayoutModeType) : 'fluid',
  }

  const [settings, setSettings] = useLocalStorage<LayoutState>('__REBACK_NEXT_CONFIG__', INIT_STATE, override)
  const safeSettings: LayoutState = {
    ...INIT_STATE,
    ...settings,
    menu: { ...INIT_STATE.menu, ...(settings?.menu ?? {}) },
  }
  const [offcanvasStates, setOffcanvasStates] = useState<LayoutOffcanvasStatesType>({
    showBackdrop: false,
  })
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  )

  const updateSettings = (_newSettings: Partial<LayoutState>) => setSettings({ ...safeSettings, ..._newSettings })

  const changeTheme = (newTheme: ThemeType) => {
    updateSettings({ theme: newTheme })
  }

  const changeTopbarTheme = (newTheme: TopBarThemeType) => {
    updateSettings({ topbarTheme: newTheme })
  }

  const changeLayoutMode = (newMode: LayoutModeType) => {
    updateSettings({ layoutMode: newMode })
  }

  const changeMenuTheme = (newTheme: MenuType['theme']) => {
    updateSettings({ menu: { ...safeSettings.menu, theme: newTheme } })
  }

  const changeMenuSize = (newSize: MenuType['size']) => {
    updateSettings({ menu: { ...safeSettings.menu, size: newSize } })
  }

  const closeSidebar = useCallback(() => {
    document.documentElement.classList.remove('sidebar-enable')
    setOffcanvasStates((prev) => ({ ...prev, showBackdrop: false }))
  }, [])

  const toggleBackdrop = useCallback(() => {
    setOffcanvasStates((prev) => {
      const nextOpen = !prev.showBackdrop
      if (nextOpen) {
        document.documentElement.classList.add('sidebar-enable')
      } else {
        document.documentElement.classList.remove('sidebar-enable')
      }
      return { ...prev, showBackdrop: nextOpen }
    })
  }, [])

  useEffect(() => {
    const MOBILE_BREAKPOINT = 768
    const checkMobile = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (!isMobile) return
    closeSidebar()
  }, [isMobile, closeSidebar])

  useEffect(() => {
    toggleDocumentAttribute('data-bs-theme', safeSettings.theme)
    toggleDocumentAttribute('data-topbar-color', safeSettings.topbarTheme)
    toggleDocumentAttribute('data-menu-color', safeSettings.menu.theme)
    toggleDocumentAttribute('data-sidenav-size', safeSettings.menu.size)
    toggleDocumentAttribute('data-layout-mode', safeSettings.layoutMode)

    return () => {
      toggleDocumentAttribute('data-bs-theme', safeSettings.theme, true)
      toggleDocumentAttribute('data-topbar-color', safeSettings.topbarTheme, true)
      toggleDocumentAttribute('data-sidenav-size', safeSettings.menu.theme, true)
      toggleDocumentAttribute('data-layout-mode', safeSettings.layoutMode, true)
    }
  }, [safeSettings])

  const resetSettings = () => updateSettings(INIT_STATE)

  return (
    <ThemeContext.Provider
      value={useMemo(
        () => ({
          ...safeSettings,
          themeMode: safeSettings.theme,
          isMobile,
          changeTheme,
          changeLayoutMode,
          changeTopbarTheme,
          changeMenu: {
            theme: changeMenuTheme,
            size: changeMenuSize,
          },
          toggleBackdrop,
          closeSidebar,
          resetSettings,
        }),
        [safeSettings, offcanvasStates, isMobile, closeSidebar],
      )}>
      {children}
      {offcanvasStates.showBackdrop && <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop} />}
    </ThemeContext.Provider>
  )
}

export { LayoutProvider, useLayoutContext }
